import { type Schedule } from "./types";
import { useEffect, useState } from "react";
import { useAppStore } from "./appStore";
import {
  Card,
  Container,
  Row,
  Col,
  Spinner,
  Alert,
  Button,
  CloseButton,
} from "react-bootstrap";
import { useUser } from "@clerk/clerk-react";
import { messagePerMonthFromSchedule } from "./util";

export function getMonthlyUsage(
  schedules: import("./types").Schedule[],
): number {
  return schedules.reduce((sum, s) => sum + messagePerMonthFromSchedule(s), 0);
}

export function ScheduledList() {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { user } = useUser();
  const appStore = useAppStore();
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState<Schedule[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [monthlyUsage, setMonthlyUsage] = useState(0);
  const [messageSentThisMonth, setMessageSentThisMonth] = useState(0);
  const [plan, setPlan] = useState<"free" | "plus" | "pro">("free");
  const [maxMessages, setMaxMessages] = useState(0);

  useEffect(() => {
    async function fetchScheduled() {
      setLoading(true);
      setError(null);
      try {
        if (!user?.id) throw new Error("User not found");
        const schedules = await appStore.getSchedules(user.id);
        setMessages(schedules);
        setMonthlyUsage(getMonthlyUsage(schedules));
        const res = await appStore.getMessageSentThisMonth(user.id);
        console.log(`Fetched message sent this month for user ${user.id}:`, res);
        setMessageSentThisMonth(res.messagesSent);
        setPlan(res.plan);
        setMaxMessages(res.maxMessages);
      } catch (err: any) {
        setError(err.message || "Unknown error");
      } finally {
        setLoading(false);
      }
    }
    if (user) fetchScheduled();
  }, [user, appStore]);

  if (!user) return null;

  const handleDelete = async (scheduleId: string) => {
    if (!user?.id) return;
    if (!window.confirm("Are you sure you want to delete this schedule?"))
      return;
    setDeletingId(scheduleId);
    try {
      const result = await appStore.deleteSchedule(user.id, scheduleId);
      if (result.success) {
        setMessages((msgs) => msgs.filter((msg) => msg.id !== scheduleId));
      } else {
        alert(result.error || "Failed to delete schedule");
      }
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <>
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
        {error && <Alert variant="danger">{error}</Alert>}
        {!loading && !error && messages.length === 0 && (
          <Alert variant="info">No scheduled messages found.</Alert>
        )}
      </>
    );
  }

  return (
    <Container className="mt-5">
      <h2 className="mb-2 text-center fw-bold text-primary">
        Your Scheduled Messages
      </h2>
      <div className="mb-4 text-center">
        <span className="fw-semibold">Estimated Monthly Usage:</span>{" "}
        {monthlyUsage} messages
      </div>
      <div className="mb-4 text-center">
        <span className="fw-semibold">Messages Sent This Month:</span>{" "}
        {messageSentThisMonth} messages
        <span className="fw-semibold ms-3">Plan:</span>{" "}
        {(plan || "free").toUpperCase()}
        <span className="fw-semibold ms-3">Monthly Limit:</span> {maxMessages}{" "}
        messages
      </div>

      <Row className="g-4">
        {messages.map((msg) => (
          <Col md={6} lg={4} key={msg.id}>
            <Card className="shadow-sm position-relative">
              <Card.Body>
                <div className="position-absolute top-0 end-0 mt-2 me-2">
                  <CloseButton
                    aria-label="Delete"
                    disabled={deletingId === msg.id}
                    onClick={() => handleDelete(msg.id)}
                    style={{ fontSize: "1.2rem" }}
                  />
                </div>
                <Card.Title>{msg.message}</Card.Title>
                <Card.Subtitle className="mb-2 text-muted">
                  To: {msg.phoneNumber}
                </Card.Subtitle>
                <div className="mb-2 text-muted">
                  Frequency: <strong>{msg.frequency}</strong>
                </div>
                {msg.date && (
                  <div>
                    <b>Date:</b> {msg.date}
                  </div>
                )}
                {msg.time && (
                  <div>
                    <b>Time:</b> {msg.time}
                  </div>
                )}
                {msg.timezone && (
                  <div>
                    <b>Timezone:</b> {msg.timezone}
                  </div>
                )}
                {msg.createdAt && (
                  <div className="text-muted small">
                    Created: {msg.createdAt}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </Container>
  );
}
